// src/hooks/useStatsNavigation.js -- navigation contract for the Stats surface.
//
// Single job: own the payload StatsPage consumes, so App.js stays a pure conductor
// (spec v1b-amendment §A5/§C3). App.js switches to the Stats tab and passes this hook's
// `statsNav` straight through as StatsPage props; it never interprets the payload. In v1b
// only the search path (requestSearchNav) is exercised -- the deep-link-unresolved fallback
// pre-fills the search field. In v1c, SlipTicket calls requestPlayerNav to open a player page
// from a slip leg. `initialPlayerLeague` was added in Deliverable B (#890): the Stats game log
// is league-qualified, so a player deep-link must carry the leg's league through to loadGameLog.

import { useState, useCallback } from 'react';

// The neutral payload: no query, no selected player, no league, no focus, no highlight.
export const EMPTY_STATS_NAV = {
  initialSearchQuery: '',
  initialPlayerId: null,
  initialPlayerLeague: null,
  initialFocusStat: null,
  highlightGameId: null,
};

export function useStatsNavigation() {
  const [statsNav, setStatsNav] = useState(EMPTY_STATS_NAV);

  // v1c deep-link: open a player page pre-focused on a market with a game
  // highlighted. focusStat/gameId are optional (default null).
  const requestPlayerNav = useCallback(({ playerId, league = null, focusStat = null, gameId = null }) => {
    setStatsNav({
      initialSearchQuery: '',
      initialPlayerId: playerId,
      initialPlayerLeague: league,
      initialFocusStat: focusStat,
      highlightGameId: gameId,
    });
  }, []);

  // v1b path: land on the search view with the query pre-filled (deep-link
  // unresolved / uncollected leg falls back here, never a guess).
  const requestSearchNav = useCallback((query) => {
    setStatsNav({ ...EMPTY_STATS_NAV, initialSearchQuery: query || '' });
  }, []);

  const clearStatsNav = useCallback(() => setStatsNav(EMPTY_STATS_NAV), []);

  return { statsNav, requestPlayerNav, requestSearchNav, clearStatsNav };
}
